import React from 'react';
import type { NextPage } from 'next';
import { getWindow, trackNavigatedToPage } from '@surfline/web-common';
import { useMount, useUnmount } from 'react-use';

import { fetchLocationView } from 'actions/map';
import { leaveGeonameMapRoute } from 'actions/routes';
import { redirectToNotFound } from 'actions/status';
import GeonameMap from 'containers/GeonameMap';
import { getOrCreateStore, type ReduxInitialProps } from 'stores';
import { useAppDispatch } from 'stores/hooks';

const GeonameMapPage: NextPage<{}, ReduxInitialProps> = () => {
  const dispatch = useAppDispatch();

  // When the map page mounts with the location in the URL, we want to sync it to the store and
  // session storage.
  useMount(() => {
    const win = getWindow();
    if (win) {
      win.document.body.classList.add('sl-hide-ze-widget', 'sl-map-page');

      trackNavigatedToPage('Map', {
        category: 'cams & forecasts',
        channel: 'cams & forecasts',
      });
    }
  });

  useUnmount(() => {
    const win = getWindow();
    win.document.body.classList.remove('sl-hide-ze-widget', 'sl-map-page');
    dispatch(leaveGeonameMapRoute());
  });

  return <GeonameMap />;
};

GeonameMapPage.getInitialProps = async (context) => {
  const store = getOrCreateStore();
  const { geonames } = context.query as { geonames: string[] };
  const { res } = context;
  // The geoname ID should always be last in the URL.
  const geonameId = geonames[geonames.length - 1];

  const isServer = !!context.req;

  // make sure the geonameId is a number
  let isGeonameIdValid = false;
  try {
    if (parseInt(geonameId, 10)) {
      isGeonameIdValid = true;
    }
  } catch (_e) {
    isGeonameIdValid = false;
  }
  if (!isGeonameIdValid) {
    store.dispatch(redirectToNotFound('The location could not be found'));
    if (isServer && res) {
      res.statusCode = 404;
    }
  } else {
    await store.dispatch(fetchLocationView(geonameId));
  }

  if (isServer) {
    const newrelic = require('newrelic');
    newrelic.addCustomAttributes({
      geonameId,
    });

    return {
      ssrReduxState: store.getState(),
    };
  }

  return {};
};

export default GeonameMapPage;
